import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Map, Loader2, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtCompleted = (iso) => {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${weekday} - ${mm}/${dd}/${yy}`;
};

const MapGroup = ({ title, payday, group, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        <p className="text-xs text-gray-400">{group.window_start} → {group.window_end} · pays {payday}</p>
      </div>
      <span className="text-sm font-bold text-emerald-700">{fmtMoney(group.total)}</span>
    </div>
    {group.maps.length === 0 ? (
      <p className="text-xs text-gray-400 py-2">No maps completed in this window yet.</p>
    ) : (
      <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
        {group.maps.map((m) => (
          <div key={m.session_id} className="flex items-center justify-between gap-3 text-sm border-b border-gray-100 last:border-0 pb-1.5 last:pb-0"
            data-testid={`completed-map-row-${m.session_id}`}>
            <div className="min-w-0">
              <p className="font-medium text-gray-800 truncate">{m.job_title}</p>
              <p className="text-xs text-gray-500 truncate">
                {m.user_name} · {fmtCompleted(m.completed_at)}
              </p>
            </div>
            <span className="font-semibold text-gray-900 whitespace-nowrap">{fmtMoney(m.value)}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const CompletedMapsThisWeek = () => {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/completed-maps-this-week`, getAuthHeader());
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <Card className="p-6" data-testid="completed-maps-week-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Map size={20} className="text-indigo-600" />
          <h2 className="text-lg font-semibold text-gray-900">This Weeks Completed Maps</h2>
        </div>
        <button type="button" onClick={() => { setLoading(true); fetchData(); }} data-testid="completed-maps-refresh-btn"
          className="text-gray-400 hover:text-gray-600" title="Refresh">
          <RefreshCw size={15} />
        </button>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-6 justify-center">
          <Loader2 className="animate-spin" size={16} /> Loading completed maps…
        </div>
      ) : !data ? (
        <div className="text-center py-6 text-gray-400 text-sm">Failed to load completed maps.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <MapGroup title="Tuesday – Thursday" payday="Friday" group={data.tue_thu} testid="completed-maps-tue-thu" />
          <MapGroup title="Friday – Monday" payday="Tuesday" group={data.fri_mon} testid="completed-maps-fri-mon" />
        </div>
      )}
    </Card>
  );
};

export default CompletedMapsThisWeek;
